// src/pages/BlogPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { designTokens } from '../theme/designTokens';
import { useBlog, useBlogList } from '../hooks/useBlog';
import BlogCard from '../components/blog/BlogCard';
import BlogSidebar from '../components/blog/BlogSidebar';
import BlogHero from '../components/blog/BlogHero';
import SEOHead from "../components/blog/SEOHead";
import {
    BookOpen,
    Users,
    PenTool,
    Feather,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    Calendar,
    Clock,
    Eye,
    Tag,
    Share2,
    Image as ImageIcon,
    X
} from 'lucide-react';
import { BlogService } from "../api/blog.api";
import {
    calculateReadingTime,
    formatBlogDate,
    generateShareUrls,
    getCategoryColor,
    parseBlogContent
} from "../utils/blogUtils";

const BlogPage = () => {
    const { slug } = useParams();

    if (slug) {
        return <BlogPostPage slug={slug} />;
    }
    return <BlogListingPage />;
};

// Single Blog Post Page
const BlogPostPage = ({ slug }) => {
    const { blog, loading, error, relatedPosts } = useBlog(slug);
    const [parsedContent, setParsedContent] = useState('');
    const [readingTime, setReadingTime] = useState(0);
    const [shareUrls, setShareUrls] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    // Function to insert images randomly into content
    const enhanceContentWithImages = useCallback((content, images) => {
        if (!images || images.length === 0 || !content) return content;

        try {
            // Create a temporary DOM element to parse the content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;

            // Get all block elements where images can be inserted
            const elements = tempDiv.querySelectorAll('p, h2, h3, h4, ul, ol, blockquote');

            if (elements.length === 0) return content;

            // Determine how many images to insert (at most 3 or half of available images)
            const imagesToInsert = Math.min(images.length, 3, Math.floor(elements.length / 2));

            // Generate random unique indices for image placement
            const insertIndices = [];
            while (insertIndices.length < imagesToInsert) {
                const randomIndex = Math.floor(Math.random() * elements.length);
                if (!insertIndices.includes(randomIndex)) {
                    insertIndices.push(randomIndex);
                }
            }

            // Sort indices in descending order to insert from bottom to top
            insertIndices.sort((a, b) => b - a);

            // Insert images at selected positions
            insertIndices.forEach((index, imgIdx) => {
                const image = images[imgIdx % images.length];
                const element = elements[index];

                // Create image container
                const imageContainer = document.createElement('div');
                imageContainer.className = 'blog-content-image';
                imageContainer.setAttribute('data-image-id', image._id || `img-${imgIdx}`);

                // Create image element
                const img = document.createElement('img');
                img.src = image.url;
                img.alt = image.alt || 'Blog image';
                img.title = image.title || '';
                img.loading = 'lazy';

                // Add click handler for image modal
                img.style.cursor = 'pointer';
                img.onclick = () => {
                    setSelectedImage(image);
                    setShowImageModal(true);
                };

                // Style the image container
                imageContainer.style.cssText = `
                    margin: 2rem 0;
                    text-align: center;
                    position: relative;
                `;

                // Create image wrapper for better styling
                const imageWrapper = document.createElement('div');
                imageWrapper.style.cssText = `
                    display: inline-block;
                    max-width: 100%;
                    border-radius: 1rem;
                    overflow: hidden;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    transition: transform 0.3s ease;
                `;

                // Add hover effect
                imageWrapper.onmouseenter = (e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                };
                imageWrapper.onmouseleave = (e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                };

                imageWrapper.appendChild(img);
                imageContainer.appendChild(imageWrapper);

                // Add caption if title exists
                if (image.title) {
                    const caption = document.createElement('figcaption');
                    caption.style.cssText = `
                        margin-top: 0.75rem;
                        font-size: 0.875rem;
                        color: ${designTokens.colors.text.tertiary};
                        font-style: italic;
                    `;
                    caption.textContent = image.title;
                    imageContainer.appendChild(caption);
                }

                // Insert after the selected element
                element.parentNode.insertBefore(imageContainer, element.nextSibling);
            });

            return tempDiv.innerHTML;
        } catch (error) {
            console.error('Error enhancing content with images:', error);
            return content;
        }
    }, []);

    // Process content when blog loads
    useEffect(() => {
        if (blog) {
            // First parse the basic content
            let content = parseBlogContent(blog.content);

            // Then enhance with random images if available
            if (blog.images && blog.images.length > 0) {
                content = enhanceContentWithImages(content, blog.images);
            }

            setParsedContent(content);

            // Calculate reading time
            const wordCount = blog.wordCount || blog.content?.split(/\s+/).length || 0;
            setReadingTime(calculateReadingTime(wordCount));

            // Generate share URLs
            setShareUrls(generateShareUrls(blog, window.location.href));

            // Increment view count
            if (blog._id) {
                BlogService.incrementBlogViews(blog._id).catch(console.error);
            }
        }
    }, [blog, enhanceContentWithImages]);

    if (loading) {
        return <BlogLoadingSkeleton />;
    }

    if (error || !blog) {
        return <BlogErrorPage error={error} />;
    }

    return (
        <>
            <SEOHead
                title={blog.metaTitle || blog.title}
                description={blog.metaDescription || blog.excerpt}
                canonicalUrl={blog.canonicalUrl || `https://kromaic.shop/blog/${blog.slug}`}
                ogImage={blog.coverImage?.url || (blog.images && blog.images[0]?.url)}
                ogType="article"
                keywords={[
                    blog.focusKeyword,
                    ...(blog.secondaryKeywords || []),
                    ...(blog.tags || [])
                ].filter(Boolean).join(', ')}
                article={{
                    publishedTime: blog.publishedAt || blog.createdAt,
                    modifiedTime: blog.updatedAt,
                    section: blog.category,
                    tags: blog.tags,
                }}
            />

            <div style={{
                background: designTokens.colors.background.primary,
                minHeight: '100vh',
            }}>
                {/* Image Modal */}
                <AnimatePresence>
                    {showImageModal && selectedImage && (
                        <ImageModal
                            image={selectedImage}
                            onClose={() => setShowImageModal(false)}
                        />
                    )}
                </AnimatePresence>

                {/* Blog Hero Section */}
                <BlogHero
                    title={blog.title}
                    excerpt={blog.excerpt}
                    category={blog.category}
                    author={blog.authorName || blog.author?.name || 'KROMAIC Team'}
                    authorAvatar={blog.author?.avatar}
                    date={blog.publishedAt || blog.createdAt}
                    readTime={readingTime}
                    views={blog.views || 0}
                    coverImage={blog.coverImage}
                />

                {/* Main Content */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 350px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '4rem 2rem',
                    gap: '3rem',
                }}>
                    {/* Article Content */}
                    <motion.article
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* Focus Keyword Highlight */}
                        {blog.focusKeyword && (
                            <div style={{
                                marginBottom: '2rem',
                                padding: '1rem',
                                background: `${designTokens.colors.champagne}10`,
                                borderRadius: '0.5rem',
                                borderLeft: `3px solid ${designTokens.colors.champagne}`,
                            }}>
                                <span style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: designTokens.colors.champagne,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Focus Keyword:
                                </span>
                                <span style={{
                                    marginLeft: '0.5rem',
                                    color: designTokens.colors.text.primary,
                                    fontWeight: 500
                                }}>
                                    {blog.focusKeyword}
                                </span>
                            </div>
                        )}

                        {/* Content Metadata */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '1.5rem',
                            marginBottom: '2rem',
                            padding: '1rem',
                            background: designTokens.colors.background.secondary,
                            borderRadius: '0.5rem',
                        }}>
                            {blog.publishedAt && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Calendar size={18} color={designTokens.colors.champagne} />
                                    <span style={{ color: designTokens.colors.text.secondary }}>
                                        Published: {formatBlogDate(blog.publishedAt)}
                                    </span>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Clock size={18} color={designTokens.colors.champagne} />
                                <span style={{ color: designTokens.colors.text.secondary }}>
                                    {readingTime} min read
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Eye size={18} color={designTokens.colors.champagne} />
                                <span style={{ color: designTokens.colors.text.secondary }}>
                                    {blog.views?.toLocaleString() || 0} views
                                </span>
                            </div>
                            {blog.images && blog.images.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <ImageIcon size={18} color={designTokens.colors.champagne} />
                                    <span style={{ color: designTokens.colors.text.secondary }}>
                                        {blog.images.length} {blog.images.length === 1 ? 'image' : 'images'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Image Gallery Preview (if multiple images) */}
                        {blog.images && blog.images.length > 1 && (
                            <div style={{
                                marginBottom: '2rem',
                                padding: '1rem',
                                background: designTokens.colors.background.secondary,
                                borderRadius: '1rem',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '1rem',
                                }}>
                                    <h4 style={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: designTokens.colors.text.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}>
                                        <ImageIcon size={18} color={designTokens.colors.champagne} />
                                        Image Gallery ({blog.images.length})
                                    </h4>
                                    <span style={{
                                        fontSize: '0.875rem',
                                        color: designTokens.colors.text.tertiary,
                                    }}>
                                        Click to enlarge
                                    </span>
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                                    gap: '0.5rem',
                                }}>
                                    {blog.images.map((image, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.05 }}
                                            style={{
                                                aspectRatio: '1',
                                                borderRadius: '0.5rem',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                border: `2px solid ${selectedImage?._id === image._id ? designTokens.colors.champagne : 'transparent'}`,
                                            }}
                                            onClick={() => {
                                                setSelectedImage(image);
                                                setShowImageModal(true);
                                            }}
                                        >
                                            <img
                                                src={image.url}
                                                alt={image.alt || `Gallery image ${index + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Parsed Content with inserted images */}
                        <div
                            className="blog-content"
                            dangerouslySetInnerHTML={{ __html: parsedContent }}
                            style={blogContentStyles}
                        />

                        {/* Secondary Keywords Section */}
                        {blog.secondaryKeywords?.length > 0 && (
                            <div style={{
                                marginTop: '3rem',
                                padding: '1.5rem',
                                background: designTokens.colors.background.secondary,
                                borderRadius: '1rem',
                            }}>
                                <h4 style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: designTokens.colors.text.primary,
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <Tag size={18} color={designTokens.colors.champagne} />
                                    Related Keywords
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                }}>
                                    {blog.secondaryKeywords.map((keyword, index) => (
                                        <span
                                            key={index}
                                            style={{
                                                padding: '0.375rem 1rem',
                                                background: `${designTokens.colors.champagne}10`,
                                                color: designTokens.colors.text.secondary,
                                                borderRadius: '9999px',
                                                fontSize: '0.875rem',
                                                border: `1px solid ${designTokens.colors.champagne}30`,
                                            }}
                                        >
                                            {keyword}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {blog.tags?.length > 0 && (
                            <div style={{
                                marginTop: '2rem',
                                paddingTop: '2rem',
                                borderTop: `1px solid ${designTokens.colors.border.light}`,
                            }}>
                                <h4 style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: designTokens.colors.text.primary,
                                    marginBottom: '1rem',
                                }}>
                                    Tags:
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                }}>
                                    {blog.tags.map((tag, index) => (
                                        <button
                                            key={index}
                                            onClick={() => window.location.href = `/blog/tag/${tag}`}
                                            style={{
                                                padding: '0.375rem 1rem',
                                                borderRadius: '9999px',
                                                background: `${getCategoryColor(blog.category)}10`,
                                                color: getCategoryColor(blog.category),
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                border: 'none',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                                        >
                                            #{tag}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Share Section */}
                        <div style={{
                            marginTop: '3rem',
                            padding: '1.5rem',
                            background: designTokens.colors.background.secondary,
                            borderRadius: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '1rem',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Share2 size={20} color={designTokens.colors.champagne} />
                                <span style={{ fontWeight: 500 }}>Share this article:</span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                {Object.entries(shareUrls).map(([platform, url]) => (
                                    <a
                                        key={platform}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: 'white',
                                            borderRadius: '0.5rem',
                                            color: designTokens.colors.text.secondary,
                                            textDecoration: 'none',
                                            fontSize: '0.875rem',
                                            border: `1px solid ${designTokens.colors.border.light}`,
                                            transition: 'all 0.2s ease',
                                            textTransform: 'capitalize'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = designTokens.colors.champagne;
                                            e.target.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'white';
                                            e.target.style.color = designTokens.colors.text.secondary;
                                        }}
                                    >
                                        {platform}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </motion.article>

                    {/* Sidebar */}
                    <BlogSidebar
                        relatedPosts={relatedPosts}
                        currentCategory={blog.category}
                        currentTags={blog.tags}
                        shareUrls={shareUrls}
                        focusKeyword={blog.focusKeyword}
                        secondaryKeywords={blog.secondaryKeywords}
                    />
                </div>
            </div>
        </>
    );
};

// Image Modal Component
const ImageModal = ({ image, onClose }) => {
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [onClose]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.9)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                style={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '-3rem',
                        right: '-3rem',
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <X size={24} />
                </button>
                <img
                    src={image.url}
                    alt={image.alt || 'Enlarged image'}
                    title={image.title}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        borderRadius: '0.5rem',
                    }}
                />
                {image.title && (
                    <div style={{
                        textAlign: 'center',
                        color: 'white',
                        marginTop: '1rem',
                        fontSize: '0.875rem',
                    }}>
                        {image.title}
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

// Blog Listing Page (keep existing implementation)
const BlogListingPage = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);

    const {
        blogs,
        loading,
        error,
        pagination,
        updateFilters,
        filters
    } = useBlogList({
        page: 1,
        limit: 12,
        sort: '-publishedAt',
        status: 'published'
    });

    useEffect(() => {
        loadCategories();
        loadTags();
    }, []);

    const loadCategories = async () => {
        try {
            const res = await BlogService.getBlogCategories();
            if (res.success) setCategories(res.data);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadTags = async () => {
        try {
            const res = await BlogService.getTagsCloud();
            if (res.success) setTags(res.data);
        } catch (error) {
            console.error('Failed to load tags:', error);
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setSelectedTag(null);
        updateFilters({
            category: category === 'all' ? undefined : category,
            tag: undefined,
            page: 1
        });
    };

    const handleTagClick = (tag) => {
        setSelectedTag(tag);
        setSelectedCategory('all');
        updateFilters({
            category: undefined,
            tag: tag,
            page: 1
        });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        updateFilters({ search: searchInput, page: 1 });
    };

    const handlePageChange = (page) => {
        updateFilters({ page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearFilters = () => {
        setSelectedCategory('all');
        setSelectedTag(null);
        setSearchInput('');
        updateFilters({
            category: undefined,
            tag: undefined,
            search: undefined,
            page: 1
        });
    };

    const stats = [
        { icon: <BookOpen size={24} />, value: pagination?.totalBlogs || '150+', label: 'Articles Published' },
        { icon: <Users size={24} />, value: '50K+', label: 'Monthly Readers' },
        { icon: <PenTool size={24} />, value: '25+', label: 'Expert Contributors' },
        { icon: <Feather size={24} />, value: '98%', label: 'Positive Feedback' },
    ];

    return (
        <>
            <SEOHead
                title="KROMAIC Journal | Luxury Cosmetics & Skincare Insights"
                description="Where science meets soul. Explore insights on mindful beauty, botanical intelligence, and emotional wellness."
                ogImage="https://images.unsplash.com/photo-1556228578-9c360e1d8d34"
                keywords="skincare, beauty, wellness, cosmetics, anti-aging, ingredients"
            />

            <div style={{
                background: designTokens.colors.background.primary,
                minHeight: '100vh',
            }}>
                {/* Hero Section */}
                <div style={{
                    background: designTokens.colors.gradients.luxury,
                    padding: '6rem 2rem 4rem',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `radial-gradient(circle at 20% 30%, ${designTokens.colors.champagne}05 2px, transparent 2px)`,
                        backgroundSize: '50px 50px',
                        opacity: 0.2,
                    }}/>

                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        position: 'relative',
                        zIndex: 1,
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            style={{
                                textAlign: 'center',
                                marginBottom: '4rem',
                            }}
                        >
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '9999px',
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: `1px solid rgba(255, 255, 255, 0.2)`,
                                marginBottom: '1.5rem',
                            }}>
                                <BookOpen size={20} color={designTokens.colors.champagne} />
                                <span style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    color: designTokens.colors.champagne,
                                }}>
                                    Knowledge & Inspiration
                                </span>
                            </div>

                            <h1 style={{
                                fontFamily: designTokens.typography.fontFamilies.display,
                                fontSize: 'clamp(3rem, 6vw, 4.5rem)',
                                fontWeight: 300,
                                color: designTokens.colors.text.primary,
                                lineHeight: 1.1,
                                marginBottom: '1.5rem',
                                letterSpacing: '-0.02em',
                            }}>
                                The KROMAIC
                                <br/>
                                <span style={{
                                    fontStyle: 'italic',
                                    color: designTokens.colors.sage,
                                }}>
                                    Journal
                                </span>
                            </h1>

                            <p style={{
                                fontSize: '1.25rem',
                                color: designTokens.colors.text.secondary,
                                maxWidth: '600px',
                                margin: '0 auto',
                                lineHeight: 1.6,
                            }}>
                                Where science meets soul. Explore insights on mindful beauty,
                                botanical intelligence, and emotional wellness.
                            </p>
                        </motion.div>

                        {/* Search Bar */}
                        {/*<motion.div*/}
                        {/*    initial={{ opacity: 0, y: 40 }}*/}
                        {/*    animate={{ opacity: 1, y: 0 }}*/}
                        {/*    transition={{ duration: 0.8, delay: 0.2 }}*/}
                        {/*    style={{*/}
                        {/*        maxWidth: '600px',*/}
                        {/*        margin: '0 auto 3rem',*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    <form onSubmit={handleSearch}>*/}
                        {/*        <div style={{*/}
                        {/*            position: 'relative',*/}
                        {/*            background: 'rgba(255, 255, 255, 0.9)',*/}
                        {/*            backdropFilter: 'blur(10px)',*/}
                        {/*            borderRadius: '9999px',*/}
                        {/*            border: `1px solid ${designTokens.colors.border.light}`,*/}
                        {/*            overflow: 'hidden',*/}
                        {/*        }}>*/}
                        {/*            <input*/}
                        {/*                type="text"*/}
                        {/*                placeholder="Search articles, topics, or keywords..."*/}
                        {/*                value={searchInput}*/}
                        {/*                onChange={(e) => setSearchInput(e.target.value)}*/}
                        {/*                style={{*/}
                        {/*                    width: '100%',*/}
                        {/*                    padding: '1rem 3rem 1rem 1.5rem',*/}
                        {/*                    background: 'transparent',*/}
                        {/*                    border: 'none',*/}
                        {/*                    fontSize: '1rem',*/}
                        {/*                    color: designTokens.colors.text.primary,*/}
                        {/*                    outline: 'none',*/}
                        {/*                }}*/}
                        {/*            />*/}
                        {/*            <button*/}
                        {/*                type="submit"*/}
                        {/*                style={{*/}
                        {/*                    position: 'absolute',*/}
                        {/*                    right: '0.5rem',*/}
                        {/*                    top: '50%',*/}
                        {/*                    transform: 'translateY(-50%)',*/}
                        {/*                    width: '40px',*/}
                        {/*                    height: '40px',*/}
                        {/*                    borderRadius: '50%',*/}
                        {/*                    background: designTokens.colors.champagne,*/}
                        {/*                    border: 'none',*/}
                        {/*                    color: 'white',*/}
                        {/*                    display: 'flex',*/}
                        {/*                    alignItems: 'center',*/}
                        {/*                    justifyContent: 'center',*/}
                        {/*                    cursor: 'pointer',*/}
                        {/*                    transition: 'all 0.2s ease',*/}
                        {/*                }}*/}
                        {/*                onMouseEnter={(e) => e.target.style.opacity = '0.9'}*/}
                        {/*                onMouseLeave={(e) => e.target.style.opacity = '1'}*/}
                        {/*            >*/}
                        {/*                <Search size={20} />*/}
                        {/*            </button>*/}
                        {/*        </div>*/}
                        {/*    </form>*/}
                        {/*</motion.div>*/}

                        {/* Stats */}
                        {/*<motion.div*/}
                        {/*    initial={{ opacity: 0, y: 40 }}*/}
                        {/*    animate={{ opacity: 1, y: 0 }}*/}
                        {/*    transition={{ duration: 0.8, delay: 0.3 }}*/}
                        {/*    style={{*/}
                        {/*        display: 'grid',*/}
                        {/*        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',*/}
                        {/*        gap: '2rem',*/}
                        {/*        maxWidth: '800px',*/}
                        {/*        margin: '0 auto',*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    {stats.map((stat, index) => (*/}
                        {/*        <div*/}
                        {/*            key={index}*/}
                        {/*            style={{*/}
                        {/*                textAlign: 'center',*/}
                        {/*                padding: '1.5rem',*/}
                        {/*                background: 'rgba(255, 255, 255, 0.9)',*/}
                        {/*                backdropFilter: 'blur(10px)',*/}
                        {/*                borderRadius: '1rem',*/}
                        {/*                border: `1px solid ${designTokens.colors.border.light}`,*/}
                        {/*                boxShadow: designTokens.shadows.subtle,*/}
                        {/*            }}*/}
                        {/*        >*/}
                        {/*            <div style={{*/}
                        {/*                display: 'flex',*/}
                        {/*                alignItems: 'center',*/}
                        {/*                justifyContent: 'center',*/}
                        {/*                width: '3rem',*/}
                        {/*                height: '3rem',*/}
                        {/*                borderRadius: '50%',*/}
                        {/*                background: `${designTokens.colors.champagne}15`,*/}
                        {/*                margin: '0 auto 1rem',*/}
                        {/*            }}>*/}
                        {/*                <div style={{ color: designTokens.colors.champagne }}>*/}
                        {/*                    {stat.icon}*/}
                        {/*                </div>*/}
                        {/*            </div>*/}
                        {/*            <div style={{*/}
                        {/*                fontSize: '1.5rem',*/}
                        {/*                fontWeight: 300,*/}
                        {/*                color: designTokens.colors.text.primary,*/}
                        {/*                marginBottom: '0.25rem',*/}
                        {/*            }}>*/}
                        {/*                {stat.value}*/}
                        {/*            </div>*/}
                        {/*            <div style={{*/}
                        {/*                fontSize: '0.875rem',*/}
                        {/*                color: designTokens.colors.text.secondary,*/}
                        {/*            }}>*/}
                        {/*                {stat.label}*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    ))}*/}
                        {/*</motion.div>*/}
                    </div>
                </div>

                {/* Main Content */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '4rem auto',
                    padding: '0 2rem',
                }}>
                    {/* Filter Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '2rem',
                        flexWrap: 'wrap',
                        gap: '1rem',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <h2 style={{
                                fontFamily: designTokens.typography.fontFamilies.display,
                                fontSize: '2rem',
                                fontWeight: 300,
                                color: designTokens.colors.text.primary,
                            }}>
                                Latest Articles
                            </h2>
                            {(selectedCategory !== 'all' || selectedTag || searchInput) && (
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: 'transparent',
                                        border: `1px solid ${designTokens.colors.border.light}`,
                                        borderRadius: '0.5rem',
                                        color: designTokens.colors.text.secondary,
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                background: 'transparent',
                                border: `1px solid ${designTokens.colors.border.light}`,
                                borderRadius: '0.5rem',
                                color: designTokens.colors.text.secondary,
                                cursor: 'pointer',
                            }}
                        >
                            <Filter size={18} />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </button>
                    </div>

                    {/* Filters */}
                    {showFilters && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{
                                marginBottom: '3rem',
                                padding: '2rem',
                                background: designTokens.colors.background.secondary,
                                borderRadius: '1rem',
                            }}
                        >
                            {/* Categories */}
                            <div style={{ marginBottom: '2rem' }}>
                                <h4 style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: designTokens.colors.text.primary,
                                    marginBottom: '1rem',
                                }}>
                                    Categories
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                }}>
                                    <CategoryButton
                                        category="all"
                                        label="All"
                                        isActive={selectedCategory === 'all' && !selectedTag}
                                        onClick={() => handleCategoryChange('all')}
                                    />
                                    {categories.map((cat) => (
                                        <CategoryButton
                                            key={cat._id}
                                            category={cat._id}
                                            label={cat._id.charAt(0).toUpperCase() + cat._id.slice(1)}
                                            count={cat.count}
                                            isActive={selectedCategory === cat._id}
                                            onClick={() => handleCategoryChange(cat._id)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Popular Tags */}
                            {tags.length > 0 && (
                                <div>
                                    <h4 style={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: designTokens.colors.text.primary,
                                        marginBottom: '1rem',
                                    }}>
                                        Popular Tags
                                    </h4>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem',
                                    }}>
                                        {tags.slice(0, 20).map((tag, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleTagClick(tag._id)}
                                                style={{
                                                    padding: '0.375rem 1rem',
                                                    borderRadius: '9999px',
                                                    background: selectedTag === tag._id
                                                        ? designTokens.colors.champagne
                                                        : `${designTokens.colors.champagne}10`,
                                                    color: selectedTag === tag._id
                                                        ? 'white'
                                                        : designTokens.colors.text.secondary,
                                                    border: 'none',
                                                    fontSize: '0.875rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                #{tag._id} ({tag.count})
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Active Filters Display */}
                    {(filters.search || selectedTag) && (
                        <div style={{
                            marginBottom: '2rem',
                            padding: '1rem',
                            background: `${designTokens.colors.champagne}10`,
                            borderRadius: '0.5rem',
                        }}>
                            <span style={{ color: designTokens.colors.text.secondary }}>
                                {filters.search && `Search: "${filters.search}"`}
                                {filters.search && selectedTag && ' • '}
                                {selectedTag && `Tag: #${selectedTag}`}
                            </span>
                        </div>
                    )}

                    {/* Blog Grid */}
                    {loading ? (
                        <BlogGridSkeleton />
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '4rem' }}>
                            <p style={{ color: designTokens.colors.text.secondary }}>
                                {error}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: '2rem',
                            }}>
                                {blogs.length > 0 ? (
                                    blogs.map((blog) => (
                                        <BlogCard
                                            key={blog._id}
                                            blog={blog}
                                            onClick={() => navigate(`/blog/${blog.slug}`)}
                                        />
                                    ))
                                ) : (
                                    <div style={{
                                        gridColumn: '1 / -1',
                                        textAlign: 'center',
                                        padding: '4rem',
                                    }}>
                                        <h3 style={{
                                            fontFamily: designTokens.typography.fontFamilies.display,
                                            fontSize: '1.5rem',
                                            fontWeight: 300,
                                            color: designTokens.colors.text.primary,
                                            marginBottom: '1rem',
                                        }}>
                                            No articles found
                                        </h3>
                                        <p style={{
                                            color: designTokens.colors.text.secondary,
                                        }}>
                                            Try adjusting your search or filter criteria
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination */}
                            {pagination && pagination.totalPages > 1 && (
                                <Pagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

// Category Button Component
const CategoryButton = ({ category, label, count, isActive, onClick }) => {
    const categoryColor = getCategoryColor(category);

    return (
        <button
            onClick={onClick}
            style={{
                padding: '0.5rem 1.5rem',
                borderRadius: '9999px',
                background: isActive ? categoryColor : 'transparent',
                border: `1px solid ${isActive ? categoryColor : designTokens.colors.border.light}`,
                color: isActive ? 'white' : designTokens.colors.text.secondary,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
                if (!isActive) {
                    e.target.style.background = `${categoryColor}15`;
                    e.target.style.borderColor = categoryColor;
                }
            }}
            onMouseLeave={(e) => {
                if (!isActive) {
                    e.target.style.background = 'transparent';
                    e.target.style.borderColor = designTokens.colors.border.light;
                }
            }}
        >
            {label}
            {count && <span style={{ opacity: 0.7 }}>({count})</span>}
        </button>
    );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        const halfVisible = Math.floor(maxVisible / 2);

        let start = Math.max(1, currentPage - halfVisible);
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '4rem',
            gap: '0.5rem',
        }}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    background: 'transparent',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    color: currentPage === 1
                        ? designTokens.colors.text.tertiary
                        : designTokens.colors.text.secondary,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                }}
            >
                <ChevronLeft size={16} />
                Previous
            </button>

            {getPageNumbers().map((page) => (
                <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        background: currentPage === page
                            ? designTokens.colors.champagne
                            : 'transparent',
                        border: `1px solid ${designTokens.colors.border.light}`,
                        color: currentPage === page
                            ? 'white'
                            : designTokens.colors.text.secondary,
                        cursor: 'pointer',
                        minWidth: '40px',
                    }}
                >
                    {page}
                </button>
            ))}

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    background: 'transparent',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    color: currentPage === totalPages
                        ? designTokens.colors.text.tertiary
                        : designTokens.colors.text.secondary,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                }}
            >
                Next
                <ChevronRight size={16} />
            </button>
        </div>
    );
};

// Blog Content Styles
const blogContentStyles = {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: designTokens.colors.text.primary,
    '& h1': {
        fontFamily: designTokens.typography.fontFamilies.display,
        fontSize: '2.5rem',
        fontWeight: 300,
        margin: '2.5rem 0 1rem',
        color: designTokens.colors.text.primary,
    },
    '& h2': {
        fontFamily: designTokens.typography.fontFamilies.display,
        fontSize: '2rem',
        fontWeight: 400,
        margin: '2rem 0 1rem',
        color: designTokens.colors.text.primary,
    },
    '& h3': {
        fontFamily: designTokens.typography.fontFamilies.display,
        fontSize: '1.5rem',
        fontWeight: 400,
        margin: '1.5rem 0 1rem',
        color: designTokens.colors.text.primary,
    },
    '& h4': {
        fontSize: '1.25rem',
        fontWeight: 600,
        margin: '1.5rem 0 1rem',
        color: designTokens.colors.text.primary,
    },
    '& p': {
        marginBottom: '1.5rem',
        color: designTokens.colors.text.secondary,
    },
    '& img': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '0.5rem',
        margin: '1.5rem 0',
    },
    '& ul, & ol': {
        margin: '1rem 0',
        paddingLeft: '1.5rem',
        color: designTokens.colors.text.secondary,
    },
    '& li': {
        marginBottom: '0.5rem',
    },
    '& blockquote': {
        borderLeft: `3px solid ${designTokens.colors.champagne}`,
        paddingLeft: '1.5rem',
        margin: '1.5rem 0',
        fontStyle: 'italic',
        color: designTokens.colors.text.secondary,
        background: `${designTokens.colors.champagne}05`,
        padding: '1.5rem',
        borderRadius: '0.5rem',
    },
    '& a': {
        color: designTokens.colors.champagne,
        textDecoration: 'none',
        borderBottom: `1px solid ${designTokens.colors.champagne}30`,
        transition: 'all 0.2s ease',
    },
    '& a:hover': {
        borderBottomColor: designTokens.colors.champagne,
    },
    '& code': {
        background: designTokens.colors.background.secondary,
        padding: '0.2rem 0.4rem',
        borderRadius: '0.25rem',
        fontSize: '0.875em',
        fontFamily: 'monospace',
    },
    '& pre': {
        background: designTokens.colors.background.secondary,
        padding: '1rem',
        borderRadius: '0.5rem',
        overflow: 'auto',
        margin: '1.5rem 0',
    },
    '& pre code': {
        background: 'none',
        padding: 0,
    },
    // Styles for inserted images
    '& .blog-content-image': {
        margin: '2rem 0',
        textAlign: 'center',
    },
    '& .blog-content-image img': {
        cursor: 'pointer',
        transition: 'transform 0.3s ease',
    },
    '& .blog-content-image img:hover': {
        transform: 'scale(1.02)',
    },
};

// Loading Skeleton
const BlogLoadingSkeleton = () => (
    <div style={{
        padding: '4rem 2rem',
        textAlign: 'center',
        background: designTokens.colors.background.primary,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: `3px solid ${designTokens.colors.champagne}30`,
            borderTopColor: designTokens.colors.champagne,
            animation: 'spin 1s linear infinite',
        }} />
    </div>
);

// Error Page
const BlogErrorPage = ({ error }) => (
    <div style={{
        padding: '4rem 2rem',
        textAlign: 'center',
        background: designTokens.colors.background.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        <h2 style={{
            fontFamily: designTokens.typography.fontFamilies.display,
            fontSize: '2rem',
            fontWeight: 300,
            marginBottom: '1rem',
            color: designTokens.colors.text.primary,
        }}>
            Article Not Found
        </h2>
        <p style={{
            color: designTokens.colors.text.secondary,
            marginBottom: '2rem',
            maxWidth: '400px',
        }}>
            {error || 'The article you are looking for does not exist or has been moved.'}
        </p>
        <div style={{ display: 'flex', gap: '1rem' }}>
            <button
                onClick={() => window.history.back()}
                style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    background: 'transparent',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    color: designTokens.colors.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                }}
            >
                Go Back
            </button>
            <button
                onClick={() => window.location.href = '/blog'}
                style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    background: designTokens.colors.champagne,
                    color: 'white',
                    border: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                }}
            >
                Browse Articles
            </button>
        </div>
    </div>
);

// Blog Grid Skeleton
const BlogGridSkeleton = () => (
    <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '2rem',
    }}>
        {[...Array(6)].map((_, index) => (
            <div
                key={index}
                style={{
                    height: '400px',
                    background: designTokens.colors.background.secondary,
                    borderRadius: '1rem',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
            />
        ))}
    </div>
);

// Add global keyframe animations
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
    `;
    document.head.appendChild(style);
}

export default BlogPage;